import { ControlledInput, ControlledTagInput, InputMasks, Stack } from '@ui-components/neo.tax';
import { ReactElement, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useAscContext } from '@neo.tax/asc/context';
import { useUpdateAscProject, type AscProject } from '@neo.tax/asc/hooks';
import { noopFormSubmit } from '@neo.tax/shared';

import {
  isCompleteMaskedDate,
  isRealMaskedDate,
  LAUNCH_DATE_ERROR,
  makeDefaultFormValues,
  maskedDateToIsoDate,
  type AscProjectMatchingForm,
} from './asc-project-matching-pane.utils';
import { CapitalizableProjectClusteringSection } from './CapitalizableProjectClusteringSection';
import { useAscProjectMatchingAnalyticIds } from './hooks';

interface AscProjectMatchingPaneProps {
  ascProject: AscProject;
}

export const AscProjectMatchingPane = ({ ascProject }: AscProjectMatchingPaneProps): ReactElement => {
  const { fiscalYearId, periodName } = useAscContext();
  const { launchDateId, teamsId } = useAscProjectMatchingAnalyticIds();
  const { clearErrors, control, getValues, reset, setError, watch } = useForm<AscProjectMatchingForm>({
    defaultValues: makeDefaultFormValues(ascProject),
  });

  const [updateAscProject] = useUpdateAscProject({
    ascProject,
    fiscalYearId,
    periodName,
    context: {
      successToast: { title: 'Project updated successfully' },
      errorToast: {
        title: 'Error updating project',
        description: 'Something unexpected prevented your changes from saving. Please try again.',
      },
    },
  });

  const handleLaunchDateBlur = (): void => {
    const launchDate = getValues('launchDate');
    if (launchDate && !isRealMaskedDate(launchDate)) {
      setError('launchDate', { type: 'validate', message: LAUNCH_DATE_ERROR });
      return;
    }
    if (!launchDate && ascProject.customFieldLaunchDate) {
      updateAscProject({ customFieldLaunchDate: null });
    }
  };

  useEffect(() => {
    reset(makeDefaultFormValues(ascProject));
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [ascProject.id, reset]);

  // Each field saves independently rather than through `handleSubmit`: whole-form
  // submission lets a launch-date error silently block and drop a team edit, and
  // its revalidation wipes the manually-set launch-date error.
  useEffect(() => {
    const subscription = watch((_values, { name }) => {
      // `name` is an RHF field path, so a tag edit arrives as `customFieldTeams.0`.
      if (name?.startsWith('customFieldTeams')) {
        const teams = getValues('customFieldTeams');
        // Empty persists as null so the pipeline falls back to teams derived from the tickets.
        updateAscProject({ customFieldTeams: teams.length > 0 ? teams : null });
        return;
      }
      if (name !== 'launchDate') {
        return;
      }
      const launchDate = getValues('launchDate');
      if (isCompleteMaskedDate(launchDate) && !isRealMaskedDate(launchDate)) {
        setError('launchDate', { type: 'validate', message: LAUNCH_DATE_ERROR });
        return;
      }
      clearErrors('launchDate');
      if (isRealMaskedDate(launchDate)) {
        updateAscProject({ customFieldLaunchDate: maskedDateToIsoDate(launchDate) });
      }
    });
    return (): void => subscription.unsubscribe();
  }, [clearErrors, getValues, setError, updateAscProject, watch]);

  return (
    <Stack direction="column" paddingX={4} paddingY={4}>
      <Stack as="form" direction="column" onSubmit={noopFormSubmit}>
        <ControlledTagInput
          control={control}
          name="customFieldTeams"
          id={teamsId}
          label="Matching Teams"
          placeholder="Type a team, then Enter or comma"
        />
        <ControlledInput
          control={control}
          name="launchDate"
          id={launchDateId}
          label="Launch Date"
          mask={InputMasks.DATE}
          maskReturnValueType="value"
          placeholder="mm/dd/yyyy"
          helpText="Used for matching only. Not a capitalization date."
          onBlur={handleLaunchDateBlur}
        />
      </Stack>
      <CapitalizableProjectClusteringSection ascProject={ascProject} />
    </Stack>
  );
};
